import React, { useEffect, useState } from 'react';
import { LuLoader, LuPlus, LuSave, LuTrash2, LuX } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

/**
 * EditDiscoveryStepsModal
 * Edit the test steps of a manual / AI generated test case, same as the
 * edit steps flow of the UI Discovery review step (PUT /crawler/update/steps).
 */
const EditDiscoveryStepsModal = ({ isOpen, onClose, onUpdated, testCase }) => {
  const [steps, setSteps] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setSteps(toArray(testCase?.steps?.step_description).map((s) => String(s)));
    setError(null);
  }, [isOpen, testCase]);

  const handleStepTextChange = (stepIndex, value) => {
    setSteps((prev) => prev.map((s, i) => (i === stepIndex ? value : s)));
  };

  const handleAddStep = () => {
    setSteps((prev) => [...prev, '']);
  };

  const handleAddStepAfter = (stepIndex) => {
    setSteps((prev) => {
      const next = [...prev];
      next.splice(stepIndex + 1, 0, '');
      return next;
    });
  };

  const handleDeleteStep = (stepIndex) => {
    setSteps((prev) => prev.filter((_, i) => i !== stepIndex));
  };

  const handleClose = () => {
    if (isUpdating) return;
    onClose();
  };

  const handleUpdate = async () => {
    const cleanedSteps = steps.map((s) => s.trim()).filter(Boolean);
    if (cleanedSteps.length === 0) {
      setError('Please add at least one test step');
      return;
    }

    setIsUpdating(true);
    setError(null);
    try {
      await axiosInstance.put(
        `/crawler/update/steps?test_case_id=${testCase.id}`,
        {
          step_description: cleanedSteps,
          expected_result: toArray(testCase?.steps?.expected_result),
          prerequisites: toArray(testCase?.steps?.prerequisites),
          additional_notes: toArray(testCase?.steps?.additional_notes)
        }
      );
      onUpdated?.(testCase);
      onClose();
    } catch (err) {
      console.error('Error updating test steps:', err);
      setError(err.response?.data?.message || 'Failed to update test steps. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen || !testCase) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 13000 }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ zIndex: 14000 }}>
        {/* Header */}
        <div className="rt-modal__header flex-shrink-0 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="truncate">Edit Test Steps: {testCase.name}</h2>
            <p>Modify, add, or remove the test steps for this test case</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            aria-label="Close"
            className="rt-modal__close disabled:opacity-50"
          >
            <LuX size={20} />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-700">Test Steps</h4>
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
              {steps.length} step{steps.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            {steps.map((step, stepIndex) => (
              <div
                key={stepIndex}
                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400"
              >
                <span className="mt-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">
                  {stepIndex + 1}
                </span>
                <textarea
                  value={step}
                  onChange={(e) => handleStepTextChange(stepIndex, e.target.value)}
                  rows={2}
                  placeholder={`Describe step ${stepIndex + 1}...`}
                  disabled={isUpdating}
                  className="min-h-[38px] flex-1 resize-y border-0 bg-transparent px-1 py-1.5 text-sm text-gray-800 outline-none placeholder:text-gray-400"
                />
                <div className="mt-1 flex flex-shrink-0 flex-col items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleAddStepAfter(stepIndex)}
                    disabled={isUpdating}
                    title="Add step below"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                  >
                    <LuPlus size={15} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteStep(stepIndex)}
                    disabled={isUpdating}
                    title="Delete step"
                    className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <LuTrash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {steps.length === 0 && (
            <p className="mt-2 rounded-lg border border-dashed border-gray-300 bg-white py-4 text-center text-xs text-gray-400">
              No steps yet. Add your first step below.
            </p>
          )}

          <button
            type="button"
            onClick={handleAddStep}
            disabled={isUpdating}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
          >
            <LuPlus size={14} /> Add Step
          </button>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white px-6 py-4 flex justify-end gap-2 border-t flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUpdating
              ? <><LuLoader size={14} className="animate-spin" /> Updating...</>
              : <><LuSave size={14} /> Update</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditDiscoveryStepsModal;
